class SiteHeader {
	constructor(el) {
		this.el = el;
		this.headerHeight = 0;

		this.megaMenuLinks = el.querySelectorAll('.mega-menu-item');
		this.mobileLinks = el.querySelectorAll('.mobile-top-level-link-clickable');
		this.mobileContainer = el.querySelector('.site-header__mobile-container');
		this.mobileMenuButton = el.querySelector('.site-header__mobile-menu-button');
		this.mobileInertElements = [];
		this.megaMenuBg = document.querySelector('.site-header__megamenu-bg');

		if (this.mobileContainer) {
			this.mobileContainer.inert = true;
		}

		this.updateBannerBackgroundClass();
		this.updateHeaderHeightVar();
		this.setupEventHandlers();
	}

	updateBannerBackgroundClass() {
		if (!document.body.classList.contains('post-type-archive-resource')) {
			return;
		}

		const banner = document.querySelector('.banner-default');

		document.body.classList.toggle('has-banner-secondary', banner?.classList.contains('bg-secondary'));
	}

	getFocusableElements(container) {
		if (!container) {
			return [];
		}

		return Array.from(
			container.querySelectorAll(
				'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
			)
		).filter((el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true');
	}

	closeDesktopMegaMenus() {
		window.clearTimeout(this.megaMenuCloseTimer);
		const body = document.querySelector('body');

		this.megaMenuLinks.forEach((megaMenuLink) => {
			megaMenuLink.setAttribute('aria-expanded', 'false');
			megaMenuLink.classList.remove('active');
		});

		body?.classList.remove('no-scroll');
		this.megaMenuBg?.classList.remove('active');
	}

	setPageContentInert(isInert) {
		if (isInert) {
			this.mobileInertElements = Array.from(document.body.children)
				.filter((element) => element !== this.el && !element.contains(this.el))
				.map((element) => ({
					element,
					wasInert: element.inert
				}));

			this.mobileInertElements.forEach(({ element }) => {
				element.inert = true;
			});
			return;
		}

		this.mobileInertElements.forEach(({ element, wasInert }) => {
			element.inert = wasInert;
		});
		this.mobileInertElements = [];
	}

	setMobileMenuOpen(isOpen, returnFocus = false) {
		document.documentElement.classList.toggle('mobile-menu-active', isOpen);
		this.mobileMenuButton?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

		if (this.mobileContainer) {
			this.mobileContainer.inert = !isOpen;
		}

		this.setPageContentInert(isOpen);

		if (isOpen) {
			this.getFocusableElements(this.mobileContainer)[0]?.focus();
			return;
		}

		this.closeMobileMegaPanels();

		if (returnFocus) {
			this.mobileMenuButton?.focus();
		}
	}

	setupEventHandlers() {
		const body = document.querySelector('body');
		const btnMobileMenu = this.mobileMenuButton;

		if (btnMobileMenu) {
			btnMobileMenu.addEventListener('click', () => {
				const isOpening = !document.documentElement.classList.contains('mobile-menu-active');

				this.setMobileMenuOpen(isOpening);
			});
		}

		if (this.mobileLinks) {
			this.mobileLinks.forEach((link) => {
				link.addEventListener('click', (e) => {
					e.preventDefault();
					this.openMobileMegaPanel(link);
				});
			});
		}

		if (this.mobileContainer) {
			this.mobileContainer.querySelectorAll('.mobile-mega-panel__back').forEach((backBtn) => {
				backBtn.addEventListener('click', (e) => {
					e.preventDefault();
					this.closeMobileMegaPanels();
				});
			});
		}

		window.addEventListener('resize', () => this.updateHeaderHeightVar());
		window.addEventListener('scroll', () => this.updateHeaderHeightVar());

		if (this.megaMenuLinks) {
			this.megaMenuLinks.forEach((link) => {
				const panel = document.getElementById(link.getAttribute('aria-controls'));
				const hoverEnabled = () => window.matchMedia('(min-width: 1025px) and (hover: hover) and (pointer: fine)').matches;
				const cancelClose = () => window.clearTimeout(this.megaMenuCloseTimer);
				const closeOnLeave = (event) => {
					if (!hoverEnabled() || this.el.contains(event.relatedTarget) || panel?.contains(event.relatedTarget)) return;
					this.megaMenuCloseTimer = window.setTimeout(() => this.closeDesktopMegaMenus(), 150);
				};
				link.addEventListener('mouseenter', () => {
					if (!hoverEnabled()) return;
					cancelClose();
					this.closeDesktopMegaMenus();
					body.classList.add('no-scroll');
					link.setAttribute('aria-expanded', 'true');
					link.classList.add('active');
					this.megaMenuBg?.classList.add('active');
				});
				link.addEventListener('mouseleave', closeOnLeave);
				panel?.addEventListener('mouseenter', cancelClose);
				panel?.addEventListener('mouseleave', closeOnLeave);
				link.addEventListener('click', () => {
					this.megaMenuLinks.forEach((megaMenuLink) => {
						if (megaMenuLink !== link) {
							megaMenuLink.setAttribute('aria-expanded', 'false');
							megaMenuLink.classList.remove('active');
						}
					});

					if (link.classList.contains('active')) {
						this.closeDesktopMegaMenus();
					} else {
						body.classList.add('no-scroll');
						link.setAttribute('aria-expanded', 'true');
						link.classList.add('active');
						this.megaMenuBg?.classList.add('active');
					}
				});
			});
		}

		this.el.addEventListener('mouseenter', () => window.clearTimeout(this.megaMenuCloseTimer));
		this.el.addEventListener('mouseleave', (event) => {
			if (!window.matchMedia('(min-width: 1025px) and (hover: hover) and (pointer: fine)').matches) return;
			const activeLink = Array.from(this.megaMenuLinks).find((link) => link.classList.contains('active'));
			const panel = activeLink && document.getElementById(activeLink.getAttribute('aria-controls'));
			if (!activeLink || panel?.contains(event.relatedTarget)) return;
			window.clearTimeout(this.megaMenuCloseTimer);
			this.megaMenuCloseTimer = window.setTimeout(() => this.closeDesktopMegaMenus(), 150);
		});

		if (this.megaMenuBg) {
			this.megaMenuBg.addEventListener('click', () => {
				this.closeDesktopMegaMenus();
			});
		}

		// Close desktop mega menu when Tab moves focus past its last link
		// (or Shift+Tab leaves the trigger/panel entirely).
		document.addEventListener('focusin', (evt) => {
			const activeTrigger = Array.from(this.megaMenuLinks).find((link) =>
				link.classList.contains('active')
			);

			if (!activeTrigger) {
				return;
			}

			const panel = activeTrigger.nextElementSibling;
			const focusTarget = evt.target;

			if (
				activeTrigger === focusTarget ||
				activeTrigger.contains(focusTarget) ||
				(panel && panel.classList.contains('mega-menu-content') && panel.contains(focusTarget))
			) {
				return;
			}

			this.closeDesktopMegaMenus();
		});

		document.addEventListener('keydown', (evt) => {
			const isMobileMenuOpen = document.documentElement.classList.contains(
				'mobile-menu-active'
			);

			if (evt.key === 'Tab' && isMobileMenuOpen) {
				const focusables = [
					...this.getFocusableElements(this.el.querySelector('.site-header__mobile-actions')),
					...this.getFocusableElements(this.mobileContainer)
				].filter((element) => element.offsetParent !== null);
				const firstFocusable = focusables[0];
				const lastFocusable = focusables[focusables.length - 1];

				if (firstFocusable && lastFocusable) {
					if (evt.shiftKey && document.activeElement === firstFocusable) {
						evt.preventDefault();
						lastFocusable.focus();
					} else if (!evt.shiftKey && document.activeElement === lastFocusable) {
						evt.preventDefault();
						firstFocusable.focus();
					}
				}
				return;
			}

			if (evt.key === 'Tab') {
				const activeTrigger = Array.from(this.megaMenuLinks).find((link) =>
					link.classList.contains('active')
				);

				if (activeTrigger) {
					const panel = activeTrigger.nextElementSibling;
					const scope = [activeTrigger];

					if (panel && panel.classList.contains('mega-menu-content')) {
						scope.push(...this.getFocusableElements(panel));
					}

					const focusables = scope.filter(Boolean);
					const currentIndex = focusables.indexOf(document.activeElement);

					if (currentIndex !== -1) {
						const leavingForward =
							!evt.shiftKey && currentIndex === focusables.length - 1;
						const leavingBackward = evt.shiftKey && currentIndex === 0;

						if (leavingForward || leavingBackward) {
							this.closeDesktopMegaMenus();
						}
					}
				}
			}

			if (evt.key !== 'Escape') {
				return;
			}

			if (isMobileMenuOpen) {
				this.setMobileMenuOpen(false, true);
				return;
			}

			const activeTrigger = Array.from(this.megaMenuLinks).find((link) =>
				link.classList.contains('active')
			);

			if (!activeTrigger) {
				return;
			}

			this.closeDesktopMegaMenus();
			activeTrigger.focus();
		});
	}

	openMobileMegaPanel(link) {
		const panel = link.nextElementSibling;

		if (!panel || !panel.classList.contains('mobile-mega-panel') || !this.mobileContainer) {
			return;
		}

		this.closeMobileMegaPanels();

		link.classList.add('active');
		link.setAttribute('aria-expanded', 'true');
		panel.classList.add('is-open');
		this.mobileContainer.classList.add('mobile-mega-panel-active');
		this.mobileContainer.scrollTop = 0;
	}

	closeMobileMegaPanels() {
		if (!this.mobileContainer) {
			return;
		}

		this.mobileLinks.forEach((link) => {
			link.classList.remove('active');
			link.setAttribute('aria-expanded', 'false');
		});

		this.mobileContainer.querySelectorAll('.mobile-mega-panel.is-open').forEach((panel) => {
			panel.classList.remove('is-open');
		});

		this.mobileContainer.classList.remove('mobile-mega-panel-active');
	}

	updateHeaderHeightVar() {
		this.headerHeight = this.el.offsetHeight;

		if (window.innerWidth >= 1025 && document.documentElement.classList.contains('mobile-menu-active')) {
			this.setMobileMenuOpen(false);
		}
	}
}

export default SiteHeader;
